import { useCallback, useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import { showSuccessToast, showErrorToast } from '@/utils/toast.utils';
import { hideModal } from '@/store/thunks/ui/uiThunk';
import { createFolderThunk } from '@/store/thunks/sixDetail/createFolderThunk';
import { selectCurrentFolderPath, selectSixId } from '@/store/selectors/sixDetail';
import { selectIsCreateFolderModal } from '@/store/selectors/ui';

interface UseCreateFolderModalState {
  isOpen: boolean;
  folderName: string;
  isSubmitting: boolean;
}

interface UseCreateFolderModalHandlers {
  handleOpenChange: (nextOpen: boolean) => void;
  handleCreate: () => void;
  handleFolderNameChange: (value: string) => void;
}

interface UseCreateFolderModalReturn {
  state: UseCreateFolderModalState;
  handlers: UseCreateFolderModalHandlers;
}

export function useCreateFolderModal(): UseCreateFolderModalReturn {
  const dispatch = useAppDispatch();

  const isOpen = useAppSelector(selectIsCreateFolderModal);
  const sixId = useAppSelector(selectSixId);
  const currentFolderPath = useAppSelector(selectCurrentFolderPath);

  const [folderName, setFolderName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setFolderName('');
    setIsSubmitting(false);
  }, [isOpen]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean): void => {
      if (!nextOpen) dispatch(hideModal());
    },
    [dispatch],
  );

  const handleCreate = useCallback((): void => {
    if (sixId === undefined) return;

    const trimmed = folderName.trim();
    if (trimmed.length === 0) return;

    const folderPath = currentFolderPath !== undefined
      ? `${currentFolderPath}/${trimmed}`
      : `/${trimmed}`;

    setIsSubmitting(true);

    void dispatch(createFolderThunk({ sixId, folderName: trimmed, folderPath }))
      .unwrap()
      .then(() => {
        showSuccessToast('Folder created successfully');
      })
      .catch(() => {
        showErrorToast('Failed to create folder');
      })
      .finally(() => {
        setIsSubmitting(false);
        dispatch(hideModal());
      });
  }, [currentFolderPath, dispatch, sixId, folderName]);

  const handleFolderNameChange = useCallback((value: string): void => {
    setFolderName(value);
  }, []);

  return {
    state: {
      isOpen,
      folderName,
      isSubmitting,
    },
    handlers: {
      handleOpenChange,
      handleCreate,
      handleFolderNameChange,
    },
  };
}
